import { Divider, Group, LoadingOverlay, Radio, Select, SelectProps, Skeleton, Stack } from '@mantine/core';
import { t } from 'i18next';
import { Fragment, useMemo, useRef } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AgentFinancialResponse, AgentSource } from '../../../api/events-client/events-client.type';
import { ContentPlaceholder } from '../../../components/content-placeholder/content-placeholder';
import { formatCurrency, getCurrency } from '../../../helpers/currency-helpers';
import { formatMonthDate } from '../../../helpers/date-time-helpers/formatting/formatting';
import { exportChart } from '../../../helpers/image-downloader/image-downloader';
import { useAgenticProcessStore } from '../../../store/agentic-process-store/agentic-process-store.hooks';
import { ChartType } from '../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { baseBlack, inkBase, secondaryBase, secondaryLight } from '../../../styles/design-tokens';
import { Text } from '../../../ui-library/typography/typography';
import { maybeGetNullLabelForSeries } from '../../financials/charts/allocation-of-costs/allocation-of-costs.helpers';
import { StyledButton } from '../../financials/charts/shared/chart-display-type-button-set';
import { CustomTooltip } from '../../financials/charts/shared/custom-tooltip';
import { assignColorsToItems, getItemColor } from '../../financials/charts/shared/shared.helpers';
import { DEFAULT_CHART_COLORS } from '../../financials/constants';
import { LineChartDot } from '../../process-analysis/components/line-chart/line-chart-dot';
import { AGENT_SOURCES_ICONS, AGENT_SOURCES_LABELS } from '../agentic-process.type';
import { getAgenticFinancialsChartData } from './agentic-financials-chart.helpers';

type AgenticFinancialsChartProps = {
  data: AgentFinancialResponse | undefined;
  isPending?: boolean;
  onShowFilters?: () => void;
};

export const AgenticFinancialsChart = ({ data, isPending = false, onShowFilters }: AgenticFinancialsChartProps) => {
  const {
    selectedChartCategory,
    selectedChartOptions,
    selectedChartType,
    setSelectedChartType,
    viewBy,
    setViewBy,
    selectedAgentSource,
    setSelectedAgentSource,
  } = useAgenticProcessStore();
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);
  const agentSourceOptions = useMemo(() => {
    if (!data?.agent_sources) {
      return [{ value: 'all', label: 'All Sources' }];
    }
    return [
      { value: 'all', label: 'All Sources' },
      ...data.agent_sources.map((source) => ({
        value: source as string,
        label: AGENT_SOURCES_LABELS[source] || source,
      })),
    ];
  }, [data]);
  const renderSelectOption: SelectProps['renderOption'] = ({ option }) => (
    <Group flex="1" gap="xs">
      {option.value !== 'all' && <img src={AGENT_SOURCES_ICONS[option.value as AgentSource]} height={12} />}
      <Text size="small">{option.label}</Text>
    </Group>
  );

  const chartRef = useRef<HTMLDivElement>(null!);

  const chartData = useMemo(() => {
    if (!data) return [];
    return getAgenticFinancialsChartData(data, selectedChartCategory, selectedAgentSource, viewBy);
  }, [data, selectedChartCategory, selectedAgentSource, viewBy]);

  const colorAssignments = assignColorsToItems(selectedChartOptions || [], DEFAULT_CHART_COLORS);

  const sharedChartComponents = (
    <>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="date" tickFormatter={(value) => formatMonthDate(value)} tick={{ fontSize: 12 }} />
      <YAxis
        yAxisId="cost"
        orientation="left"
        tick={{ fontSize: 12 }}
        tickFormatter={viewBy === 'cost' ? (value) => formatCurrency(value, { currency: currencyCode }) : undefined}
        label={viewBy === 'cost' ? { value: currencyCode, position: 'insideLeft', offset: 0, angle: -90 } : undefined}
      />
      <Tooltip
        content={
          <CustomTooltip
            filterFn={(item) => (selectedChartOptions || []).includes(item.name || '')}
            nameFormatter={(item) => maybeGetNullLabelForSeries(item.name!)}
            valueFormatter={(item) =>
              viewBy === 'cost'
                ? formatCurrency(Number(item.value), { currency: currencyCode })
                : Number(item.value).toLocaleString()
            }
            getStrokeColor={(item) => getItemColor(item.name!, colorAssignments)}
          />
        }
      />
    </>
  );

  const chart = useMemo(() => {
    if (isPending) {
      return <Skeleton />;
    }

    switch (selectedChartType) {
      case ChartType.Line:
        return (
          <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: 160 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                {sharedChartComponents}
                {(selectedChartOptions || []).map((option) => {
                  const color = getItemColor(option, colorAssignments);
                  return (
                    <Fragment key={option}>
                      <Line
                        yAxisId="cost"
                        type="monotone"
                        dataKey={option}
                        stroke={color}
                        strokeWidth={2}
                        opacity={1}
                        fill={color}
                        key={option}
                        dot={<LineChartDot color={color} />}
                      />
                    </Fragment>
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      case ChartType.Bar:
        return (
          <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: 160 }}>
            <ResponsiveContainer>
              <ComposedChart data={chartData}>
                {sharedChartComponents}
                {(selectedChartOptions || []).map((option) => {
                  const color = getItemColor(option, colorAssignments);
                  return (
                    <Fragment key={option}>
                      <Bar yAxisId="cost" dataKey={option} fill={color} opacity={1} key={option} />
                    </Fragment>
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );
      default:
        return null;
    }
  }, [chartData, selectedChartType, isPending, selectedChartOptions, colorAssignments, sharedChartComponents]);

  return (
    <Stack h="100%" style={{ overflow: 'hidden', position: 'relative' }}>
      <LoadingOverlay visible={isPending} loaderProps={{ color: secondaryBase }} />
      {!isPending && chartData.length === 0 ? (
        <ContentPlaceholder icon="event_busy" text={t('common.labels.no_filtered_data')} />
      ) : (
        <Fragment>
          <Group mb={12} justify="space-between">
            <Group>
              <StyledButton
                label="Line Chart"
                iconName="stacked_line_chart"
                onClick={() => setSelectedChartType(ChartType.Line)}
                selected={selectedChartType === ChartType.Line}
              />
              <StyledButton
                label="Bar Chart"
                iconName="bar_chart"
                onClick={() => setSelectedChartType(ChartType.Bar)}
                selected={selectedChartType === ChartType.Bar}
              />
              {onShowFilters && (
                <StyledButton label="Filters" iconName="filter_list" onClick={onShowFilters} selected={false} />
              )}
            </Group>
            <Group>
              <Group gap="xs">
                <Text size="small">Agent Source</Text>
                <Select
                  value={selectedAgentSource}
                  data={agentSourceOptions}
                  onChange={(value) => setSelectedAgentSource(value as AgentSource | 'all')}
                  style={{ width: '150px' }}
                  radius="xl"
                  renderOption={renderSelectOption}
                  allowDeselect={false}
                />
              </Group>
              <Divider orientation="vertical" />
              <Group>
                <Radio.Group
                  onChange={(value) => setViewBy(value as 'cost' | 'tokens')}
                  value={viewBy}
                  styles={{ label: { fontWeight: 600, marginLeft: '30px' } }}
                >
                  <Group>
                    <Radio
                      value="cost"
                      label="Cost"
                      variant="outline"
                      color={secondaryLight}
                      styles={{
                        radio: { cursor: 'pointer' },
                        label: { fontWeight: 550, color: viewBy === 'cost' ? baseBlack : inkBase },
                      }}
                    />
                    <Radio
                      value="tokens"
                      label="Tokens"
                      variant="outline"
                      color={secondaryLight}
                      styles={{
                        radio: { cursor: 'pointer' },
                        label: { fontWeight: 550, color: viewBy === 'tokens' ? baseBlack : inkBase },
                      }}
                    />
                  </Group>
                </Radio.Group>
                <StyledButton
                  label="Export"
                  iconName="download"
                  onClick={() => exportChart(chartRef.current, 'agentic-financials-chart.png')}
                  selected={true}
                />
              </Group>
            </Group>
          </Group>

          {chart}
        </Fragment>
      )}
    </Stack>
  );
};
